import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";
import { STUDY_RESOURCE_TYPE, STUDY_RESOURCE_TYPE_IDS } from "../_constants/studyResourceType";

export interface LearningModule {
  id: number;
  name: string;
}

export interface StudyResource {
  id?: number;
  name: string;
  typeId: number;
  subTypeId?: number | null;
  url?: string | null;
  content?: string | null;
  instructorId?: number | null;
  rubricId?: number | null;
  rubricName?: string;
  hasAiConfig?: boolean;
  isRagified?: boolean;
  config?: {
    testId?: number;
    'player-format'?: string;
    [key: string]: any;
  };
  metaData?: {
    totalPages?: number;
    [key: string]: any;
  };
  creationDate?: string;
  lastModifiedDate?: string;
  typeName?: string;
  duration?: number;
  fileSizeInByte?: number;
  learningModules?: LearningModule[];
  vimeoVideoId?: string;
  vimeoVideoUri?: string;
  vimeoVideoMetaData?: any;
  // [discussion-board type=20] board fields — saved in study_discussion_board, joined on Id
  description?: string | null;
  active?: boolean;
  acceptStudentPosts?: boolean;
  acceptCommentFromRoleIds?: string | null;
  icon?: string | null;
  sendToEmails?: string | null;
  // [discussion-board type=20] notification toggle — persisted to study_resource_master.Config.sendEmails
  sendEmails?: boolean;
}

export interface StudyResourceListResponse {
  success: boolean;
  data: StudyResource[];
  count: number;
  totalCount: number;
  limit: number;
  offset: number;
}

export interface StudyResourceResponse {
  success: boolean;
  data?: StudyResource;
  message?: string;
  id?: number;
}

export interface VimeoUploadResponse {
  success: boolean;
  message: string;
  data: {
    url: string;
    videoId: string;
    videoUri: string;
    playerUrl: string;
  };
}

export interface Test {
  id: number;
  name: string;
  testName?: string;
  categoryId?: number;
  categoryName?: string;
  createdDate?: string;
  status?: string;
}

class StudyResourceService {
  /**
   * Get all study resources with optional filters
   */
  async getStudyResources(params?: {
    typeId?: number;
    search?: string;
    id?: number;
    ragStatus?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }): Promise<StudyResourceListResponse> {
    const url = `${Config.nodeSiteUrl}v1/study-resources`;
    const response = await myAxios.get(url, { params });
    return response.data;
  }

  /**
   * Get a single study resource by ID
   */
  async getStudyResourceById(id: number): Promise<StudyResourceResponse> {
    const url = `${Config.nodeSiteUrl}v1/study-resources/${id}`;
    const response = await myAxios.get(url);
    return response.data;
  }

  /**
   * Create a new study resource
   */
  async createStudyResource(resource: StudyResource): Promise<StudyResourceResponse> {
    const url = `${Config.nodeSiteUrl}v1/study-resources`;
    const response = await myAxios.post(url, resource);
    return response.data as unknown as StudyResourceResponse;
  }

  /**
   * Update an existing study resource
   */
  async updateStudyResource(id: number, resource: Partial<StudyResource>): Promise<StudyResourceResponse> {
    const url = `${Config.nodeSiteUrl}v1/study-resources/${id}`;
    const response = await myAxios.put(url, resource);
    return response.data as unknown as StudyResourceResponse;
  }

  /**
   * Delete a study resource
   */
  async deleteStudyResource(id: number, hardDelete: boolean = false): Promise<StudyResourceResponse> {
    const queryString = hardDelete ? "?hardDelete=true" : "";
    const url = `${Config.nodeSiteUrl}v1/study-resources/${id}${queryString}`;
    const response = await myAxios.delete(url);
    return response.data as unknown as StudyResourceResponse;
  }

  /**
   * Assign or unassign a rubric to a study resource
   * @param resourceId - The ID of the study resource
   * @param rubricId - The ID of the rubric to assign, or null to unassign
   */
  async assignRubricToResource(
    resourceId: number,
    rubricId: number | null
  ): Promise<StudyResourceResponse> {
    const url = `${Config.nodeSiteUrl}v1/study-resources/${resourceId}/rubric`;
    const response = await myAxios.patch(url, { rubricId });
    return response.data as unknown as StudyResourceResponse;
  }

  /**
   * Upload a video file to Vimeo
   */
  async uploadVideoToVimeo(file: File, onProgress?: (progress: number) => void): Promise<VimeoUploadResponse> {
    const formData = new FormData();
    formData.append("video", file);

    const url = `${Config.nodeSiteUrl}v1/study-resources/upload-vimeo`;
    const response = await myAxios.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    });

    return response.data as unknown as VimeoUploadResponse;
  }

  /**
   * Upload a file to S3 (for Download type resources)
   */
  async uploadFileToS3(file: File, onProgress?: (progress: number) => void): Promise<VimeoUploadResponse> {
    const formData = new FormData();
    formData.append("file", file);

    const url = `${Config.nodeSiteUrl}v1/study-resources/upload-file`;
    const response = await myAxios.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    });

    return response.data as unknown as VimeoUploadResponse;
  }

  /**
   * Upload a file from Google Drive to S3 (backend downloads from Drive, uploads to S3)
   */
  async uploadFromGoogleDrive(
    fileId: string,
    fileName: string,
    accessToken: string,
  ): Promise<{ success: boolean; data: { url: string; filename: string; size: number } }> {
    const url = `${Config.nodeSiteUrl}v1/study-resources/upload-from-drive`;
    const response = await myAxios.post(url, { fileId, fileName, accessToken });
    return response.data as any;
  }

  /**
   * Get enabled study resource types from master table
   */
  async getStudyResourceTypes(): Promise<{ id: number; name: string }[]> {
    const url = `${Config.nodeSiteUrl}v1/study-resources/types`;
    const response = await myAxios.get(url);
    return response.data;
  }

  /**
   * Get resource type name from ID
   */
  getResourceTypeName(typeId: number): string {
    const types: { [key: number]: string } = {
      1: "Download",
      5: "Reference",
      6: "Article",
      13: "E-Book",
      27: "SCORM Course",
      31: "Vimeo Video",
      32: "YouTube Video",
      36: "Online Test",
      40: "Interactive Pathway",
      41: "Spin Wheel Game",
    };
    return types[typeId] || "Unknown";
  }

  /**
   * Extract filename from URL
   */
  extractFilename(url: string): string {
    if (!url) return "Uploaded File";

    try {
      // Extract filename from S3 URL or any URL
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const parts = pathname.split('/');
      const filename = parts[parts.length - 1];

      // Remove timestamp prefix if exists (e.g., "1770812305954-meanmedianmode.pdf" -> "meanmedianmode.pdf")
      const match = filename.match(/^\d+-(.+)$/);
      return match ? decodeURIComponent(match[1]) : decodeURIComponent(filename);
    } catch (e) {
      // If URL parsing fails, try to extract from path
      const parts = url.split('/');
      const filename = parts[parts.length - 1];
      const match = filename.match(/^\d+-(.+)$/);
      return match ? decodeURIComponent(match[1]) : decodeURIComponent(filename);
    }
  }

  /**
   * Get available tests for selection
   */
  async getAvailableTests(params?: { search?: string }): Promise<Test[]> {
    // Get instructor ID from authentication service
    const currentUserStr = localStorage.getItem('currentUser');
    let instructorId = null;

    console.log('🔐 Checking authentication...');
    console.log('🔐 currentUser exists:', !!currentUserStr);

    if (currentUserStr) {
      try {
        const currentUser = JSON.parse(currentUserStr);
        instructorId = currentUser?.instructorId;
        console.log('🔐 Token exists:', !!currentUser?.token);
        console.log('🔐 Token length:', currentUser?.token?.length || 0);
        console.log('👤 User ID:', currentUser?.userId);
        console.log('👤 Instructor ID:', instructorId);
      } catch (e) {
        console.error('Failed to parse currentUser:', e);
      }
    } else {
      console.error('❌ No currentUser in localStorage - user not logged in!');
    }

    if (!instructorId) {
      console.warn('⚠️ No instructor ID found (likely logged in as admin/root).');
      console.warn('⚠️ Cannot fetch tests via instructor endpoint.');
      console.warn('⚠️ To enable test browsing for admin users, you need to:');
      console.warn('  1. Add a backend API endpoint that returns all tests, OR');
      console.warn('  2. Use a batch-specific endpoint');
      return [];
    }

    // Use new Node.js endpoint with instructor-course-test mapping
    const url = `${Config.nodeSiteUrl}v1/reports/tests/instructor/${instructorId}`;

    console.log('🔗 Making API call to:', url);
    console.log('🔗 Instructor ID:', instructorId);
    console.log('🔗 Full URL:', url);

    try {
      const response = await myAxios.get<any>(url, { params });

      // DEBUG: Log response to understand structure
      console.log('✅ Test API response received:');
      console.log('📦 Full response:', response);
      console.log('📦 Response data:', response.data);
      console.log('📦 Response status:', response.status);

      // Map response data to Test interface
      // New Node.js API response structure: { success: true, data: [...], count: N }
      const rawData: any = response.data;
      const tests = Array.isArray(rawData?.data)
        ? rawData.data
        : (Array.isArray(rawData) ? rawData : []);

      console.log('📊 Parsed tests array:', tests);
      console.log('📊 Number of tests:', tests.length);

      if (rawData?.success === false) {
        console.error('❌ API returned error:', rawData.error);
        return [];
      }

      return tests.map((test: any) => ({
        id: test.id || test.Id || test.testId,
        name: test.name || test.Name || test.testName || 'Unnamed Test',
        testName: test.testName || test.name || test.Name,
        categoryId: test.categoryId || test.CategoryId,
        categoryName: test.categoryName || test.CategoryName,
        createdDate: test.testDate || test.TestDate || test.createdDate || test.CreatedDate,
        status: test.status || test.Status
      }));
    } catch (error) {
      console.error('Failed to fetch tests:', error);
      return [];
    }
  }

  /**
   * Ask Claude to draft "Custom Pathway Guardrails" based on the existing pathways
   * configured for an Interactive Pathway (typeId 40) resource. Returns plain text
   * suitable for the guardrails textarea.
   */
  async suggestPathwayGuardrails(pathwayConfig: any): Promise<string> {
    const url = `${Config.nodeSiteUrl}v1/gen-ai/suggest-pathway-guardrails`;
    const response = await myAxios.post(url, { pathwayConfig });
    const data: any = response.data;
    return data?.guardrails || '';
  }

  /**
   * Get a presigned URL for an S3 resource
   */
  async getSignedUrl(url: string): Promise<string> {
    const apiUrl = `${Config.nodeSiteUrl}v1/study-resources/get-signed-url`;
    const response = await myAxios.post(apiUrl, { url });
    return (response.data as any).data.url;
  }

  /**
   * Validate resource based on type
   */
  validateResource(resource: StudyResource): string | null {
    if (!resource.name || resource.name.trim() === "") {
      return "Resource name is required";
    }

    if (!resource.typeId) {
      return "Resource type is required";
    }

    if (!STUDY_RESOURCE_TYPE_IDS.includes(resource.typeId)) {
      return "Invalid resource type";
    }

    // Type 6 (Article) requires content
    if (resource.typeId === STUDY_RESOURCE_TYPE.ARTICLE && (!resource.content || resource.content.trim() === "")) {
      return "Content is required for Article type";
    }

    // [discussion-board type=20] state lives in study_discussion_board; only name is required.
    if (resource.typeId === STUDY_RESOURCE_TYPE.DISCUSSION_BOARD) {
      return null;
    }

    // Type 36 (Online Test) requires testId in config
    if (resource.typeId === STUDY_RESOURCE_TYPE.ONLINE_TEST) {
      if (!resource.config?.testId) {
        return "Test selection is required for Online Test type";
      }
      return null; // Config is valid, no URL needed
    }

    // Type 40 (Interactive Pathway) is native — no URL needed, config drives the renderer
    if (resource.typeId === STUDY_RESOURCE_TYPE.INTERACTIVE_PATHWAY) {
      return null;
    }

    // Type 41 (Spin Wheel Game) is native — wedge JSON in content drives the renderer
    if (resource.typeId === STUDY_RESOURCE_TYPE.SPIN_WHEEL) {
      if (!resource.content || resource.content.trim() === "") {
        return "Add at least one wedge to the wheel";
      }
      return null;
    }

    // Other types require URL (except Article and Online Test, handled above)
    if (
      resource.typeId !== STUDY_RESOURCE_TYPE.ARTICLE &&
      resource.typeId !== STUDY_RESOURCE_TYPE.ONLINE_TEST &&
      (!resource.url || resource.url.trim() === "")
    ) {
      return "URL is required for this resource type";
    }

    return null;
  }
}

export default new StudyResourceService();
